import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { isSuperAdmin } from '@/lib/permissions';
import type { Tables } from '@/types/database';

/**
 * Hooks that drive the super_admin Card Mapping page. RLS allows non-super
 * users to read rows where `profile_id = auth.uid()` (so the Expense
 * Tracking page can show "this is on your card") but only super_admin can
 * INSERT or DELETE. The hooks below are written for the super_admin
 * workflow; the read-side `useMyCardAssignments` is exposed for the
 * employee context.
 */

const KEY = ['card-assignments'];

export type CardAssignmentRow = Tables<'card_assignments'> & {
  profiles: Pick<Tables<'profiles'>, 'id' | 'full_name' | 'email' | 'avatar_url' | 'role'> | null;
  cards: Pick<Tables<'cards'>, 'id' | 'last_four' | 'name_on_card' | 'network' | 'status'> & {
    bank_accounts: Pick<Tables<'bank_accounts'>, 'id' | 'name' | 'account_type'> | null;
  };
};

/**
 * Super_admin: every card_assignments row in the org with the joined card
 * (incl. parent bank account) and profile context. Used to render the
 * "who owns which card" matrix on the Card Mapping page.
 */
export function useAllCardAssignments() {
  const { role, loading } = useAuth();
  const enabled = !loading && isSuperAdmin(role);
  return useQuery({
    queryKey: [...KEY, 'all'],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('card_assignments')
        .select(
          // card_assignments has TWO foreign keys to profiles (profile_id and
          // assigned_by), so PostgREST can't auto-resolve a bare `profiles(...)`
          // embed — it returns PGRST201 (ambiguous embed). We pin the embed to
          // the profile_id FK explicitly so the assignee is what we get back.
          'card_id, profile_id, organization_id, assigned_by, created_at, profiles!card_assignments_profile_id_fkey(id, full_name, email, avatar_url, role), cards(id, last_four, name_on_card, network, status, bank_accounts(id, name, account_type))',
        );
      if (error) throw error;
      return (data ?? []) as unknown as CardAssignmentRow[];
    },
  });
}

/**
 * Read-side for non-super_admin contexts ("which cards am I responsible
 * for?"). Anyone can run this — RLS will only return their own rows.
 */
export function useMyCardAssignments() {
  const { user, loading } = useAuth();
  return useQuery({
    queryKey: [...KEY, 'mine', user?.id],
    enabled: !loading && !!user?.id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('card_assignments')
        .select(
          'card_id, profile_id, organization_id, assigned_by, created_at, cards(id, last_four, name_on_card, network, status, bank_accounts(id, name, account_type))',
        )
        .eq('profile_id', user!.id);
      if (error) throw error;
      return (data ?? []) as unknown as CardAssignmentRow[];
    },
  });
}

export function useAssignCard() {
  const qc = useQueryClient();
  const { user, profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { card_id: string; profile_id: string }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const { data, error } = await supabase
        .from('card_assignments')
        .insert({
          card_id: input.card_id,
          profile_id: input.profile_id,
          organization_id: profile.organization_id,
          assigned_by: user?.id ?? null,
        })
        .select('card_id, profile_id')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUnassignCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { card_id: string; profile_id: string }) => {
      const { error } = await supabase
        .from('card_assignments')
        .delete()
        .eq('card_id', input.card_id)
        .eq('profile_id', input.profile_id);
      if (error) throw error;
      return { card_id: input.card_id, profile_id: input.profile_id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
